import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { routeTree } from "./routeTree.gen";
import { loadRuntimeConfig, type RuntimeConfig } from "@/lib/config";
import { AppAuthProvider, useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { SessionProvider, useSession } from "@/lib/session";
import { SignIn } from "@/components/auth/SignIn";
import { Splash } from "@/components/auth/Splash";
import { applyTheme, rememberedTheme, themeNamed } from "@/themes";
import "@/styles/app.css";

// Before anything renders: the look this browser last showed, so the sign-in
// screen and the splash match the org the person will land in. The session
// corrects it if that has changed (src/themes/useTheme.ts).
applyTheme(rememberedTheme());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Everything below the sign-in gate.
 *
 * Signed in to the identity provider is not yet signed in to an org: the ID
 * token is exchanged for a session first, and the router mounts only once one
 * exists, so no route loader can fire a request that is guaranteed to 401.
 */
function AuthedApp() {
  const auth = useAuth();

  if (auth.isLoading) return <Splash message="Signing in…" />;

  if (auth.error) {
    return <SignIn error={auth.error} onSignIn={() => auth.signIn()} />;
  }

  if (!auth.isAuthenticated || !auth.idToken) {
    return <SignIn onSignIn={() => auth.signIn()} />;
  }

  return (
    <SessionProvider idToken={auth.idToken} signIn={auth.signIn}>
      <SessionGate onSignIn={() => auth.signIn()} />
    </SessionProvider>
  );
}

function SessionGate({ onSignIn }: { onSignIn: () => void }) {
  const { isLoading, error, session } = useSession();

  if (error) {
    if (error instanceof ApiError && error.needsInvitation) {
      return <Splash tone="error" message={error.message} />;
    }
    if (error instanceof ApiError && error.needsSSO) {
      return (
        <Splash
          tone="error"
          message={
            "This organisation requires signing in through its own identity provider, " +
            "and that sign-in did not give this account access. Sign out and try again, " +
            "or ask an administrator."
          }
        />
      );
    }
    // The identity token itself was refused or has lapsed.
    return <SignIn error={error.message} onSignIn={onSignIn} />;
  }
  if (isLoading || !session) return <Splash message="Opening your workspace…" />;

  return <RouterProvider router={router} />;
}

function Boot() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRuntimeConfig()
      .then((cfg) => {
        // An org's own hostname knows its look before any session does.
        if (cfg.theme) applyTheme(themeNamed(cfg.theme));
        setConfig(cfg);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <Splash message={error} tone="error" />;
  if (!config) return <Splash message="Loading…" />;

  return (
    <AppAuthProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <AuthedApp />
      </QueryClientProvider>
    </AppAuthProvider>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("index.html is missing its #root element");

createRoot(rootEl).render(
  <StrictMode>
    <Boot />
  </StrictMode>,
);
